import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Users, CheckCircle, CurrencyDollar, UserPlus, FilePdf, Trash, X, UserCheck } from '@phosphor-icons/react';
import { LoadingSpinner } from '../ui/loading-spinner';
import { formatDatePST } from './utils';
import { AdminAddPlayerDialog } from './AdminAddPlayerDialog';
import { getApiBaseUrl } from '../../utils/api';
import { toast } from 'sonner';

/**
 * SessionRosterDialog - Dialog for viewing session roster
 */
export function SessionRosterDialog({
  open,
  onOpenChange,
  session,
  roster,
  loading,
  onRefresh
}) {
  const [showAddPlayer, setShowAddPlayer] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [removingPlayer, setRemovingPlayer] = useState(null);
  const [checkingInPlayer, setCheckingInPlayer] = useState(null);

  const handleCheckIn = async (entry) => {
    const playerId = entry.player?.player_id;
    if (!playerId || !session?.session_id) {
      toast.error('Missing player or session information');
      return;
    }
    
    setCheckingInPlayer(playerId);
    try {
      const response = await fetch(
        `${getApiBaseUrl()}/api/admin/checkin`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            player_id: playerId,
            session_id: session.session_id
          })
        }
      );
      
      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.detail || 'Check-in failed');
      }
      
      const result = await response.json();
      toast.success(result.message || `${entry.player?.name} checked in!`);
      onRefresh?.();
    } catch (error) {
      console.error('Check-in failed:', error);
      toast.error(error.message || 'Check-in failed');
    } finally {
      setCheckingInPlayer(null);
    }
  };

  const handleAddPlayerSuccess = () => {
    setShowAddPlayer(false);
    onRefresh?.();
  };

  const handleRemovePlayer = async (entry) => {
    // Handle both flat and nested registration_id formats
    const registrationId = entry.registration?.registration_id || entry.registration_id;
    
    if (!registrationId) {
      toast.error('Cannot remove: missing registration ID');
      return;
    }
    
    const playerName = entry.player?.name || entry.player_name || 'this player';
    if (!window.confirm(`Remove ${playerName} from this session?`)) {
      return;
    }
    
    setRemovingPlayer(registrationId);
    try {
      const response = await fetch(
        `${getApiBaseUrl()}/api/admin/registrations/${registrationId}`,
        { 
          method: 'DELETE',
          credentials: 'include' 
        }
      );
      
      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.detail || 'Failed to remove player');
      }
      
      const result = await response.json();
      toast.success(result.message || `${playerName} removed from session`);
      onRefresh?.();
    } catch (error) {
      console.error('Remove player failed:', error);
      toast.error(error.message || 'Failed to remove player');
    } finally {
      setRemovingPlayer(null);
    }
  };

  const handleDownloadPdf = async () => {
    if (!session) return;
    
    setDownloadingPdf(true);
    try {
      const response = await fetch(
        `${getApiBaseUrl()}/api/sessions/${session.session_id}/roster-pdf`,
        { credentials: 'include' }
      );
      
      if (!response.ok) {
        throw new Error('Failed to generate PDF');
      }
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `StayOnHoops_Roster_${session.date}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      toast.success('Roster PDF downloaded!');
    } catch (error) {
      console.error('PDF download failed:', error);
      toast.error('Failed to download roster PDF');
    } finally {
      setDownloadingPdf(false);
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="bg-card border-border max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center justify-between">
              <div>
                REGISTRATION ROSTER
                {session && (
                  <span className="ml-2 text-sm font-normal text-muted-foreground">
                    {formatDatePST(session.date)} • {session.location?.name}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={handleDownloadPdf}
                  disabled={downloadingPdf || roster.length === 0}
                  data-testid="download-roster-pdf-btn"
                >
                  {downloadingPdf ? (
                    <LoadingSpinner size="sm" className="mr-1" />
                  ) : (
                    <FilePdf size={16} className="mr-1" />
                  )}
                  PDF
                </Button>
                <Button
                  size="sm"
                  onClick={() => setShowAddPlayer(true)}
                  data-testid="add-player-to-session-btn"
                >
                  <UserPlus size={16} className="mr-1" />
                  Add Player
                </Button>
              </div>
            </DialogTitle>
          </DialogHeader>
          
          {loading ? (
            <div className="flex justify-center py-8">
              <LoadingSpinner size="lg" />
            </div>
          ) : roster.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Users size={48} className="mx-auto mb-3 opacity-50" />
              <p>No players registered for this session</p>
              <Button
                variant="outline"
                className="mt-4"
                onClick={() => setShowAddPlayer(true)}
              >
                <UserPlus size={16} className="mr-2" />
                Add First Player
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              <div className="flex items-center justify-between text-sm text-muted-foreground pb-2 border-b border-border">
                <span>{roster.length} registered</span>
                <span>{roster.filter(r => r.checked_in).length} checked in</span>
            </div>
            
            {roster.map((entry, index) => {
              // Handle both flat and nested data structures
              const registrationId = entry.registration?.registration_id || entry.registration_id;
              const paymentStatus = entry.registration?.payment_status || entry.payment_status;
              const registrationType = entry.registration?.registration_type || entry.registration_type;
              
              return (
              <div 
                key={registrationId || index}
                className={`p-3 rounded-lg border ${
                  paymentStatus === 'waitlisted' 
                    ? 'bg-yellow-500/10 border-yellow-500/30' 
                    : 'bg-background/50 border-border'
                }`}
                data-testid={`roster-entry-${index}`}
              >
                <div className="flex items-start justify-between">
                  <div className="space-y-1">
                    <div className="flex items-center gap-2">
                      <span className="font-medium">
                        {entry.player?.name || entry.player_name || 'Unknown Player'}
                      </span>
                      {(entry.player?.age || entry.player_age) && (
                        <span className="text-xs px-1.5 py-0.5 bg-primary/10 rounded text-primary">
                          Age {entry.player?.age || entry.player_age}
                        </span>
                      )}
                      {entry.player?.instagram_handle && entry.player.instagram_handle !== 'n/a' && (
                        <span className="text-xs text-pink-400">
                          {entry.player.instagram_handle}
                        </span>
                      )}
                      {entry.status === 'waitlisted' && (
                        <span className="text-xs px-1.5 py-0.5 bg-yellow-500/20 rounded text-yellow-500">
                          Waitlist
                        </span>
                      )}
                      {entry.admin_registration && (
                        <span className="text-xs px-1.5 py-0.5 bg-blue-500/20 rounded text-blue-400">
                          Admin Added
                        </span>
                      )}
                    </div>
                    <div className="text-sm text-muted-foreground">
                      Parent: {entry.parent?.name || 'Unknown'}
                      {entry.parent?.phone && ` • ${entry.parent.phone}`}
                    </div>
                    {entry.parent?.email && (
                      <div className="text-xs text-muted-foreground">
                        {entry.parent.email}
                      </div>
                    )}
                    {entry.notes && (
                      <div className="text-xs text-muted-foreground italic">
                        Note: {entry.notes}
                      </div>
                    )}
                  </div>
                  
                  <div className="flex items-start gap-3">
                    <div className="flex flex-col items-end gap-1">
                      {/* Payment Status */}
                      <span className={`text-xs px-2 py-0.5 rounded flex items-center gap-1 ${
                        paymentStatus === 'paid' || registrationType === 'comp' ? 'bg-green-500/20 text-green-500' :
                        registrationType === 'comp' ? 'bg-purple-500/20 text-purple-400' :
                        paymentStatus === 'invoice' ? 'bg-blue-500/20 text-blue-400' :
                        'bg-yellow-500/20 text-yellow-500'
                      }`}>
                        <CurrencyDollar size={12} />
                        {registrationType === 'comp' ? 'Comp' :
                         paymentStatus === 'paid' ? 'Paid' : 
                         paymentStatus === 'invoice' ? 'Invoice' : 'Pending'}
                      </span>
                      
                      {/* Check-in Status / Button */}
                      {entry.checked_in ? (
                        <span className="text-xs px-2 py-0.5 rounded flex items-center gap-1 bg-green-500/20 text-green-500">
                          <CheckCircle size={12} />
                          Checked In
                        </span>
                      ) : (
                        <button
                          onClick={() => handleCheckIn(entry)}
                          disabled={checkingInPlayer === entry.player?.player_id}
                          className="text-xs px-2 py-1 rounded flex items-center gap-1 bg-primary/20 text-primary hover:bg-primary/30 transition-colors disabled:opacity-50"
                          data-testid={`checkin-btn-${entry.player?.player_id}`}
                        >
                          {checkingInPlayer === entry.player?.player_id ? (
                            <LoadingSpinner size="sm" />
                          ) : (
                            <>
                              <UserCheck size={12} />
                              Check In
                            </>
                          )}
                        </button>
                      )}
                    </div>
                    
                    {/* Remove Button */}
                    <button
                      onClick={() => handleRemovePlayer(entry)}
                      disabled={removingPlayer === registrationId}
                      className="p-1.5 rounded-md text-red-400 hover:bg-red-500/20 hover:text-red-300 transition-colors disabled:opacity-50"
                      title="Remove from session"
                      data-testid={`remove-player-btn-${index}`}
                    >
                      {removingPlayer === registrationId ? (
                        <LoadingSpinner size="sm" />
                      ) : (
                        <X size={16} weight="bold" />
                      )}
                    </button>
                  </div>
                </div>
              </div>
              );
            })}
          </div>
        )}
        
        <div className="flex justify-end pt-4 border-t border-border">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>

    {/* Add Player Dialog */}
    <AdminAddPlayerDialog
      open={showAddPlayer}
      onOpenChange={setShowAddPlayer}
      session={session}
      onSuccess={handleAddPlayerSuccess}
    />
  </>
  );
}
